import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Sparkles, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { API } from "../lib/api";

const uid = () => `sess_${Math.random().toString(36).slice(2)}_${Date.now()}`;

export const AssistantModal = ({ open, onClose }) => {
  const [sessionId] = useState(uid);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      axios
        .get(`${API}/assistant/greeting`)
        .then(({ data }) => setMessages([{ role: "assistant", content: data.greeting }]))
        .catch(() =>
          setMessages([{ role: "assistant", content: "Hi, I'm Phusic's Assistant. How can I help you today?" }])
        );
    }
  }, [open, messages.length]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading || done) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/assistant/chat`, { session_id: sessionId, message: text });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
      if (data.submitted) setDone(true);
    } catch (e) {
      const msg =
        e?.response?.status === 503
          ? "The assistant isn't available right now. Please try again shortly."
          : "Sorry, something went wrong. Please try again.";
      setMessages((m) => [...m, { role: "assistant", content: msg }]);
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="assistant-modal"
          initial={{ y: 40, opacity: 0, scale: 0.98 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 30, opacity: 0, scale: 0.98 }}
          transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
          className="fixed z-[70] bottom-0 right-0 sm:bottom-6 sm:right-6 w-full sm:w-[400px] h-[85vh] sm:h-[600px] bg-surface border border-white/10 rounded-t-2xl sm:rounded-2xl overflow-hidden flex flex-col shadow-[0_8px_50px_rgba(0,0,0,0.7)]"
        >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 bg-black/40">
              <div className="flex items-center gap-3">
                <span className="h-9 w-9 rounded-full bg-crimson/15 border border-crimson/40 flex items-center justify-center">
                  <Sparkles size={16} className="text-crimson" />
                </span>
                <div>
                  <p className="font-serif text-lg leading-none text-white">Pulse</p>
                  <p className="font-body text-[11px] uppercase tracking-[0.18em] text-white/40 mt-1">
                    Phusic Assistant
                  </p>
                </div>
              </div>
              <button
                data-testid="assistant-close"
                onClick={onClose}
                className="text-white/60 hover:text-white transition-colors"
                aria-label="Close assistant"
              >
                <X size={20} />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} data-testid="assistant-messages" className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    data-testid={`assistant-msg-${m.role}`}
                    className={`max-w-[82%] rounded-2xl px-4 py-3 font-body text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-crimson text-white rounded-br-sm"
                        : "bg-white/5 text-white/85 border border-white/10 rounded-bl-sm"
                    }`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white/5 border border-white/10 rounded-2xl rounded-bl-sm px-4 py-3">
                    <div className="flex gap-1.5">
                      {[0, 1, 2].map((d) => (
                        <span
                          key={d}
                          className="h-2 w-2 rounded-full bg-white/50 animate-bounce"
                          style={{ animationDelay: `${d * 0.15}s` }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {done && (
                <div data-testid="assistant-submitted" className="flex items-center gap-2 justify-center pt-2 text-emerald-400 font-body text-xs uppercase tracking-[0.15em]">
                  <CheckCircle2 size={15} /> Inquiry submitted
                </div>
              )}
            </div>

            {/* Input */}
            <div className="border-t border-white/10 p-4 bg-black/40">
              <div className="flex items-end gap-2">
                <textarea
                  data-testid="assistant-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                  disabled={done}
                  rows={1}
                  placeholder={done ? "Conversation complete" : "Type your message…"}
                  className="flex-1 resize-none max-h-28 bg-white/5 border border-white/10 rounded-xl px-4 py-3 font-body text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-crimson/60 transition-colors disabled:opacity-50"
                />
                <button
                  data-testid="assistant-send"
                  onClick={send}
                  disabled={loading || done || !input.trim()}
                  className="h-11 w-11 shrink-0 rounded-xl bg-crimson text-white flex items-center justify-center hover:bg-crimson/85 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  aria-label="Send message"
                >
                  <Send size={17} />
                </button>
              </div>
            </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};
